import {
  AnimationProps,
  MotionProps,
  Transition,
  Variants,
} from 'framer-motion'

type AppearParams = {
  y?: number
  delay?: number
  duration?: number
  staggerChildren?: number
  viewportMargin?: string
}

type AppearGroupHandler = (params: AppearParams) => {
  motionProps: MotionProps
}
type VariantParams = {
  delay?: number
  duration?: number
  y?: number | string
  staggerChildren?: number
}

type VariantHandler = (
  params: VariantParams,
  type?: 'ease' | 'spring'
) => Variants | undefined

type AnimationParams = {
  delay?: number
  duration?: number
  y?: string | number
}
type AnimationHandler = (params: AnimationParams) => AnimationProps

export const springTransition: Transition = {
  type: 'spring',
  stiffness: 130,
  damping: 30,
  mass: 1,
}

export const easeTransition = {
  ease: [0.75, 0, 0.25, 1],
}

export const fadeSlideIn: AnimationHandler = ({ y = '56', delay }) => ({
  initial: { opacity: 0, y },
  animate: { opacity: 1, y: 0 },
  transition: { ...springTransition, delay },
})
export const slideInTopLeft: AnimationHandler = ({ delay, duration }) => ({
  initial: { x: '-100%', y: '-100%' },
  animate: { x: 0, y: 0 },
  transition: { ...easeTransition, delay, duration },
})
export const fadeInBottomRight: AnimationHandler = ({ delay, duration }) => ({
  initial: { opacity: 0, x: '100%', y: '100%' },
  animate: { opacity: 1, x: 0, y: 0 },
  transition: { ...easeTransition, delay, duration },
})
export const textAnimation: AnimationHandler = ({ delay, y = '105%' }) => ({
  initial: { y },
  animate: { y: 0 },
  transition: { ...springTransition, delay },
})
export const separatorAnimation: AnimationHandler = ({ delay, duration }) => ({
  initial: { scaleX: 0 },
  animate: { scaleX: 1 },
  transition: { ...easeTransition, delay, duration },
})

export const separatorReveal: VariantHandler = ({ duration = 1 }) => ({
  hidden: { scaleX: 0 },
  show: { scaleX: 1, transition: { ...easeTransition, duration } },
})

export const fadeReveal: VariantHandler = (
  { delay, duration, staggerChildren },
  type = 'spring'
) => {
  const transition = {
    ...(type === 'spring' ? springTransition : easeTransition),
    ...(delay && { delay }),
    ...(duration && { duration }),
    ...(staggerChildren && { staggerChildren }),
  }

  return {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition },
  }
}

export const slideReveal: VariantHandler = (
  { y = 56, delay, duration, staggerChildren },
  type = 'spring'
) => {
  const transition = {
    ...(type === 'spring' ? springTransition : easeTransition),
    ...(delay && { delay }),
    ...(duration && { duration }),
    ...(staggerChildren && { staggerChildren }),
  }

  return {
    hidden: { opacity: 0, y },
    show: { opacity: 1, y: 0, transition },
  }
}

export const scaleRevealTopRight: VariantHandler = (
  { delay, duration },
  type = 'spring'
) => {
  const transition = {
    ...(type === 'spring' ? springTransition : easeTransition),
    ...(delay && { delay }),
    ...(duration && { duration }),
  }

  return {
    hidden: { x: '10%', y: '-10%', scale: 1.2, opacity: 0 },
    show: { x: 0, y: 0, scale: 1, opacity: 1, transition },
  }
}

export const scaleRevealBottomLeft: VariantHandler = (
  { delay, duration },
  type = 'spring'
) => {
  const transition = {
    ...(type === 'spring' ? springTransition : easeTransition),
    ...(delay && { delay }),
    ...(duration && { duration }),
  }

  return {
    hidden: { scale: 1.4 },
    show: { scale: 1, transition },
  }
}

export const motionSlide: AppearGroupHandler = ({
  delay,
  y = 56,
  staggerChildren,
  viewportMargin = '-20% 0px',
}) => {
  const motionProps = {
    initial: 'hidden',
    whileInView: 'show',
    variants: slideReveal({ delay, y, staggerChildren }),
    viewport: { once: true, margin: viewportMargin },
  }

  return { motionProps }
}

export const motionGroup: AppearGroupHandler = ({
  delay,
  staggerChildren,
  viewportMargin = '-20% 0px',
}) => {
  const motionProps = {
    initial: 'hidden',
    whileInView: 'show',
    variants: fadeReveal({ delay, staggerChildren }),
    viewport: { once: true, margin: viewportMargin },
  }

  return { motionProps }
}
